import { useEffect, useState, useMemo } from 'react';
import { toast } from 'sonner';
import api, { downloadFile, getMediaBlobUrl } from '../../utils/api';
import { 
  CalendarDays, Download, Trash2, Plus, Upload, Edit, Save, 
  Search, FileText, Eye, X, Loader2, Filter, Clock, Calendar
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { useDropzone } from 'react-dropzone';
import BackButton from '../../components/BackButton';
import useAuthStore from '../../store/authStore';

// Year configuration with colors
const YEARS = [2026, 2025, 2024, 2023, 2022, 2021, 2020];
const YEAR_COLORS = {
  2026: { gradient: 'from-blue-500 to-indigo-600', badge: 'bg-blue-500', text: 'text-blue-400', border: 'border-blue-500/30' },
  2025: { gradient: 'from-emerald-500 to-teal-600', badge: 'bg-emerald-500', text: 'text-emerald-400', border: 'border-emerald-500/30' },
  2024: { gradient: 'from-violet-500 to-purple-600', badge: 'bg-violet-500', text: 'text-violet-400', border: 'border-violet-500/30' },
  2023: { gradient: 'from-amber-500 to-orange-600', badge: 'bg-amber-500', text: 'text-amber-400', border: 'border-amber-500/30' },
  2022: { gradient: 'from-rose-500 to-pink-600', badge: 'bg-rose-500', text: 'text-rose-400', border: 'border-rose-500/30' },
  2021: { gradient: 'from-cyan-500 to-sky-600', badge: 'bg-cyan-500', text: 'text-cyan-400', border: 'border-cyan-500/30' },
  2020: { gradient: 'from-slate-500 to-gray-600', badge: 'bg-slate-500', text: 'text-slate-400', border: 'border-slate-500/30' },
};

const getYearColor = (date) => {
  const year = new Date(date).getFullYear();
  return YEAR_COLORS[year] || YEAR_COLORS[2020];
};

const Agenda = () => {
  const { user } = useAuthStore();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  
  // Preview
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewItem, setPreviewItem] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  
  // Filter & Search
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedYear, setSelectedYear] = useState('all');
  
  // Form fields
  const [title, setTitle] = useState('');
  const [meetingDate, setMeetingDate] = useState('');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState(null);
  
  // Edit form fields
  const [editTitle, setEditTitle] = useState('');
  const [editMeetingDate, setEditMeetingDate] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editFile, setEditFile] = useState(null);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    maxFiles: 1,
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    },
    onDrop: (acceptedFiles) => setFile(acceptedFiles[0]),
  });

  const { getRootProps: getEditRootProps, getInputProps: getEditInputProps, isDragActive: isEditDragActive } = useDropzone({
    maxFiles: 1,
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    },
    onDrop: (acceptedFiles) => setEditFile(acceptedFiles[0]),
  });

  useEffect(() => { loadItems(); }, []);

  const loadItems = async () => {
    try {
      const response = await api.get('/agendas');
      setItems(response.data);
    } catch (error) {
      toast.error('Failed to load agendas');
    } finally {
      setLoading(false);
    }
  };

  // Filtered items
  const filteredItems = useMemo(() => {
    return items.filter(item => {
      const matchesSearch = item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           (item.description && item.description.toLowerCase().includes(searchQuery.toLowerCase()));
      const itemYear = new Date(item.meeting_date).getFullYear();
      const matchesYear = selectedYear === 'all' || itemYear === parseInt(selectedYear);
      return matchesSearch && matchesYear;
    });
  }, [items, searchQuery, selectedYear]);

  // Stats
  const stats = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return {
      total: items.length,
      thisYear: items.filter(i => new Date(i.meeting_date).getFullYear() === currentYear).length,
      withDocs: items.filter(i => i.file_url).length,
      upcoming: items.filter(i => new Date(i.meeting_date) > new Date()).length,
    };
  }, [items]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Create agenda first
      const response = await api.post('/agendas', null, {
        params: { title, meeting_date: meetingDate, description }
      });
      
      // If file was selected, upload it
      if (file && response.data.id) {
        const formData = new FormData();
        formData.append('file', file);
        await api.post(`/agendas/${response.data.id}/upload`, formData);
      }
      
      toast.success('Agenda created successfully');
      setDialogOpen(false);
      resetForm();
      loadItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create agenda');
    } finally {
      setLoading(false);
    }
  };

  const openEditDialog = (item) => {
    setEditingItem(item);
    setEditTitle(item.title);
    setEditMeetingDate(item.meeting_date.split('T')[0]);
    setEditDescription(item.description || '');
    setEditFile(null);
    setEditDialogOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editingItem) return;
    setLoading(true);
    try {
      // Update agenda metadata
      await api.put(`/agendas/${editingItem.id}`, null, {
        params: { 
          title: editTitle, 
          meeting_date: editMeetingDate, 
          description: editDescription 
        }
      });
      
      // If new file was selected, upload it
      if (editFile) {
        const formData = new FormData();
        formData.append('file', editFile);
        await api.post(`/agendas/${editingItem.id}/upload`, formData);
      }
      
      toast.success('Agenda updated successfully');
      setEditDialogOpen(false);
      setEditingItem(null);
      loadItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update agenda');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete agenda "${title}"?`)) return;
    try {
      await api.delete(`/agendas/${id}`);
      toast.success('Agenda deleted');
      loadItems();
    } catch (error) {
      toast.error('Failed to delete agenda');
    }
  };

  const resetForm = () => { 
    setTitle(''); 
    setMeetingDate(''); 
    setDescription(''); 
    setFile(null); 
  };

  const openPreview = async (item) => {
    if (!item.file_url) {
      toast.info('No document attached to this agenda');
      return;
    }
    setPreviewItem(item);
    setPreviewOpen(true);
    setPreviewLoading(true);
    try {
      const url = await getMediaBlobUrl(item.file_url);
      setPreviewUrl(url);
    } catch (error) {
      toast.error('Failed to load preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    setPreviewOpen(false);
    setPreviewItem(null);
    if (previewUrl) { window.URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }
  };

  const handleDownload = async (item) => {
    if (!item.file_url) {
      toast.info('No document attached to this agenda');
      return;
    }
    try {
      await downloadFile(item.file_url, item.file_name || item.title);
      toast.success('Download started');
    } catch (error) {
      toast.error('Download failed');
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="space-y-6" data-testid="admin-agenda-page">
      <BackButton to="/admin" />
      
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex items-center space-x-4">
          <div className="bg-gradient-to-br from-indigo-500 to-purple-600 p-4 rounded-2xl shadow-lg">
            <CalendarDays className="w-8 h-8 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-serif font-bold text-slate-900">Meeting Agendas</h1>
            <p className="text-slate-400">Schedule and manage upcoming meeting agendas</p>
          </div>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 shadow-lg" data-testid="add-agenda-btn">
              <Plus className="w-4 h-4 mr-2" />Add Agenda
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl bg-slate-800 text-white border-slate-700">
            <DialogHeader><DialogTitle className="text-white">Create New Agenda</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="add-agenda-form">
              <div className="space-y-2">
                <Label className="text-slate-300">Agenda Title</Label>
                <Input 
                  value={title} 
                  onChange={(e) => setTitle(e.target.value)} 
                  placeholder="e.g., Q1 Executive Committee Meeting" 
                  required 
                  className="bg-slate-700 border-slate-600 text-white"
                  data-testid="agenda-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Meeting Date</Label>
                <Input 
                  type="date"
                  value={meetingDate} 
                  onChange={(e) => setMeetingDate(e.target.value)} 
                  required 
                  className="bg-slate-700 border-slate-600 text-white"
                  data-testid="agenda-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Description / Agenda Points</Label>
                <textarea 
                  value={description} 
                  onChange={(e) => setDescription(e.target.value)} 
                  placeholder="List the main agenda items and discussion points..." 
                  rows={4} 
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  data-testid="agenda-description-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Attach Document (Optional)</Label>
                <div {...getRootProps()} className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${isDragActive ? 'border-indigo-500 bg-indigo-500/10' : 'border-slate-600 hover:border-slate-500 bg-slate-700/50'}`}>
                  <input {...getInputProps()} data-testid="agenda-file-input" />
                  <Upload className="w-10 h-10 mx-auto mb-3 text-slate-400" />
                  {file ? (
                    <p className="text-sm text-indigo-400">Selected: {file.name}</p>
                  ) : (
                    <div>
                      <p className="text-sm text-slate-300">Drag & drop or click to select</p>
                      <p className="text-xs text-slate-500">PDF, DOC, DOCX</p>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex space-x-2 pt-4">
                <Button type="submit" disabled={loading} className="flex-1 bg-gradient-to-r from-indigo-500 to-purple-600" data-testid="submit-agenda-btn">
                  {loading ? 'Creating...' : 'Create Agenda'}
                </Button>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">Cancel</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-indigo-500/20 to-purple-500/20 border border-indigo-500/30 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-indigo-400 text-sm font-medium">Total Agendas</p>
              <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
            </div>
            <CalendarDays className="w-8 h-8 text-indigo-500/50" />
          </div>
        </div>
        <div className="bg-gradient-to-br from-emerald-500/20 to-teal-500/20 border border-emerald-500/30 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-emerald-400 text-sm font-medium">Upcoming</p>
              <p className="text-2xl font-bold text-slate-900">{stats.upcoming}</p>
            </div>
            <Clock className="w-8 h-8 text-emerald-500/50" />
          </div>
        </div>
        <div className="bg-gradient-to-br from-blue-500/20 to-indigo-500/20 border border-blue-500/30 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-400 text-sm font-medium">This Year</p>
              <p className="text-2xl font-bold text-slate-900">{stats.thisYear}</p>
            </div>
            <Calendar className="w-8 h-8 text-blue-500/50" />
          </div>
        </div>
        <div className="bg-gradient-to-br from-amber-500/20 to-orange-500/20 border border-amber-500/30 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-amber-400 text-sm font-medium">With Documents</p>
              <p className="text-2xl font-bold text-slate-900">{stats.withDocs}</p>
            </div>
            <FileText className="w-8 h-8 text-amber-500/50" />
          </div>
        </div>
      </div>

      {/* Search & Filter */}
      <div className="flex flex-col sm:flex-row gap-4 p-4 bg-slate-800/50 rounded-xl border border-slate-700">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input 
            value={searchQuery} 
            onChange={(e) => setSearchQuery(e.target.value)} 
            placeholder="Search agendas..." 
            className="pl-10 bg-slate-700 border-slate-600 text-white"
            data-testid="agenda-search-input"
          />
          {searchQuery && <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"><X className="w-4 h-4" /></button>}
        </div>
        <div className="flex items-center space-x-2">
          <Filter className="w-5 h-5 text-slate-400" />
          <Select value={selectedYear} onValueChange={setSelectedYear}>
            <SelectTrigger className="w-36 bg-slate-700 border-slate-600 text-white" data-testid="year-filter-select">
              <SelectValue placeholder="All Years" />
            </SelectTrigger>
            <SelectContent className="bg-slate-700 border-slate-600">
              <SelectItem value="all">All Years</SelectItem>
              {YEARS.map(year => <SelectItem key={year} value={year.toString()}>{year}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Year Filter Pills */}
      <div className="flex flex-wrap gap-2">
        <button 
          onClick={() => setSelectedYear('all')} 
          className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${selectedYear === 'all' ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'}`}
        >
          All Years
        </button>
        {YEARS.map(year => {
          const colors = YEAR_COLORS[year];
          const count = items.filter(i => new Date(i.meeting_date).getFullYear() === year).length;
          return (
            <button 
              key={year} 
              onClick={() => setSelectedYear(year.toString())} 
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center space-x-2 ${selectedYear === year.toString() ? `bg-gradient-to-r ${colors.gradient} text-white shadow-lg` : 'bg-slate-700 text-slate-300 hover:bg-slate-600'}`}
            >
              <span>{year}</span>
              <span className={`text-xs ${selectedYear === year.toString() ? 'bg-white/20' : 'bg-slate-600'} px-2 py-0.5 rounded-full`}>{count}</span>
            </button>
          );
        })}
      </div>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl bg-slate-800 text-white border-slate-700">
          <DialogHeader><DialogTitle className="text-white">Edit Agenda</DialogTitle></DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-4" data-testid="edit-agenda-form">
            <div className="space-y-2">
              <Label className="text-slate-300">Agenda Title</Label>
              <Input value={editTitle} onChange={(e) => setEditTitle(e.target.value)} required className="bg-slate-700 border-slate-600 text-white" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Meeting Date</Label>
              <Input type="date" value={editMeetingDate} onChange={(e) => setEditMeetingDate(e.target.value)} required className="bg-slate-700 border-slate-600 text-white" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Description</Label>
              <textarea value={editDescription} onChange={(e) => setEditDescription(e.target.value)} rows={4} className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-white" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Replace Document (Optional)</Label>
              {editingItem?.file_url && (
                <p className="text-sm text-slate-400 mb-2">Current: {editingItem.file_name || 'Document attached'}</p>
              )}
              <div {...getEditRootProps()} className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer ${isEditDragActive ? 'border-indigo-500 bg-indigo-500/10' : 'border-slate-600 bg-slate-700/50'}`}>
                <input {...getEditInputProps()} />
                <Upload className="w-8 h-8 mx-auto mb-2 text-slate-400" />
                {editFile ? <p className="text-sm text-indigo-400">New: {editFile.name}</p> : <p className="text-sm text-slate-400">Drop to replace document</p>}
              </div>
            </div>
            <div className="flex space-x-2 pt-4">
              <Button type="submit" disabled={loading} className="flex-1 bg-gradient-to-r from-indigo-500 to-purple-600"><Save className="w-4 h-4 mr-2" />{loading ? 'Saving...' : 'Save Changes'}</Button>
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)} className="flex-1">Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Preview Modal */}
      {previewOpen && previewItem && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={closePreview}>
          <div className="relative max-w-4xl w-full max-h-[90vh] bg-slate-800 rounded-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-slate-700">
              <div>
                <h3 className="font-semibold text-white">{previewItem.title}</h3>
                <p className="text-sm text-slate-400">{formatDate(previewItem.meeting_date)}</p>
              </div>
              <div className="flex items-center space-x-2">
                <Button onClick={() => handleDownload(previewItem)} className="bg-indigo-600 hover:bg-indigo-700">
                  <Download className="w-4 h-4 mr-2" />Download
                </Button>
                <button onClick={closePreview} className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg"><X className="w-5 h-5" /></button>
              </div>
            </div>
            <div className="h-[70vh] bg-slate-900">
              {previewLoading ? (
                <div className="h-full flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-indigo-500" /></div>
              ) : previewUrl ? (
                <iframe src={previewUrl} className="w-full h-full" title="Document Preview" />
              ) : (
                <div className="h-full flex items-center justify-center text-slate-400">Preview not available. Click Download to view.</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Content Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-indigo-500" /></div>
      ) : filteredItems.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredItems.map((item) => {
            const colors = getYearColor(item.meeting_date);
            const isUpcoming = new Date(item.meeting_date) > new Date();
            return (
              <div 
                key={item.id} 
                className={`group bg-slate-800 rounded-2xl overflow-hidden border-2 ${colors.border} hover:border-indigo-500/50 transition-all duration-300 hover:shadow-xl`}
                data-testid={`agenda-card-${item.id}`}
              >
                <div className={`h-2 bg-gradient-to-r ${colors.gradient}`} />
                <div className="p-5">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-2">
                      <span className={`${colors.badge} text-white text-xs font-bold px-2 py-1 rounded-full`}>
                        {new Date(item.meeting_date).getFullYear()}
                      </span>
                      {isUpcoming && (
                        <span className="bg-emerald-500 text-white text-xs font-bold px-2 py-1 rounded-full flex items-center">
                          <Clock className="w-3 h-3 mr-1" />Upcoming
                        </span>
                      )}
                    </div>
                    <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button onClick={() => openEditDialog(item)} className="p-2 text-slate-400 hover:text-amber-400 hover:bg-slate-700 rounded-lg" title="Edit" data-testid={`edit-agenda-${item.id}`}><Edit className="w-4 h-4" /></button>
                      <button onClick={() => handleDelete(item.id, item.title)} className="p-2 text-slate-400 hover:text-red-400 hover:bg-slate-700 rounded-lg" title="Delete" data-testid={`delete-agenda-${item.id}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                  
                  <h3 className="font-semibold text-lg text-white mb-2 line-clamp-2 group-hover:text-indigo-400 transition-colors">{item.title}</h3>
                  
                  <div className="flex items-center text-sm text-slate-400 mb-3">
                    <Calendar className="w-4 h-4 mr-2" />
                    {formatDate(item.meeting_date)}
                  </div>
                  
                  {item.description && (
                    <p className="text-sm text-slate-400 mb-4 line-clamp-3">{item.description}</p>
                  )}
                  
                  {item.file_url && (
                    <div className="flex items-center text-sm text-indigo-400 mb-4">
                      <FileText className="w-4 h-4 mr-2" />
                      {item.file_name || 'Document attached'}
                    </div>
                  )}
                  
                  <div className="flex space-x-2">
                    {item.file_url ? (
                      <>
                        <Button onClick={() => openPreview(item)} variant="outline" size="sm" data-testid="preview-btn" className="flex-1 bg-slate-700 text-white border-slate-500 hover:bg-slate-600 hover:text-white hover:border-indigo-400">
                          <Eye className="w-4 h-4 mr-1" />Preview
                        </Button>
                        <Button onClick={() => handleDownload(item)} size="sm" className={`flex-1 bg-gradient-to-r ${colors.gradient}`}>
                          <Download className="w-4 h-4 mr-1" />Download
                        </Button>
                      </>
                    ) : (
                      <Button onClick={() => openEditDialog(item)} variant="outline" size="sm" className="w-full border-slate-600 hover:border-indigo-500 hover:text-indigo-400">
                        <Upload className="w-4 h-4 mr-1" />Attach Document
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-slate-800 border border-slate-700 rounded-2xl text-center py-16">
          <CalendarDays className="w-16 h-16 mx-auto mb-4 text-slate-600" />
          <p className="text-slate-400 text-lg">{selectedYear === 'all' ? 'No agendas created yet' : `No agendas for ${selectedYear}`}</p>
          <p className="text-sm text-slate-500 mt-2">Click "Add Agenda" to schedule a new meeting</p>
        </div>
      )}
    </div>
  );
};

export default Agenda;
